// src/api/client.ts
import { reportSessionAccount } from "./sessionAccount";

export type ApiErrorShape =
  | { error?: { code?: string; message?: string } }
  | { detail?: string }
  | { message?: string }
  | any;

export type ApiEnvelope<T = unknown> = {
  data?: T;
  error?: {
    message?: string;
    code?: string;
  };
  detail?: string;
  message?: string;
};

/**
 * Thrown by apiJson/apiData when the backend returns a non-2xx.
 *
 * Callers that need access to structured error payloads (e.g. FastAPI's
 * `detail` when used with a dict body) should catch ApiError and read
 * `.detail` — plain `Error` would lose that shape.
 */
export class ApiError extends Error {
  status: number;
  detail: unknown;
  body: unknown;

  constructor(message: string, opts: { status: number; detail?: unknown; body?: unknown }) {
    super(message);
    this.name = "ApiError";
    this.status = opts.status;
    this.detail = opts.detail;
    this.body = opts.body;
  }
}

/**
 * The statuses that mean "the round-trip did not complete", NOT "the server
 * refused the request".
 *
 * * 504 — `proxyJson` gave up waiting (`UPSTREAM_TIMEOUT`). The backend is
 *   still working and may well finish successfully a few seconds later.
 * * 502 — the BFF could not complete the upstream call at all (`UPSTREAM_ERROR`
 *   from the catch in `proxyJson`), or an infra layer answered with its own
 *   HTML error page.
 * * 503 — the upstream was not routable/available for this hop.
 *
 * What they share is the only thing a caller can act on: **the outcome of the
 * request is unknown**. For a read that is merely a failure; for a WRITE it is
 * not, because the write may have landed. Measured in production 2026-09-03:
 * `POST /report-channels/proposals/apply` returned 504 at 15.03s and the
 * backend committed everything successfully at 17.9s.
 *
 * Deliberately NOT in this set: 500. A 500 is the application answering that it
 * failed — a real error, and treating it as "unknown" would swallow it.
 */
const GATEWAY_STATUSES = new Set([502, 503, 504]);

/**
 * True when `err` is a gateway/timeout-class failure — see GATEWAY_STATUSES.
 *
 * Use this instead of hand-rolling a status check per call site: the two ways
 * such a failure reaches the frontend look nothing alike from the outside.
 * `apiJson` throws an `ApiError` for the BFF's JSON `{error:{code,message}}`
 * body, and ALSO for a non-JSON infra error page — the "502/504 HTML" case
 * already documented on `testAuthorizationConnection` in `authorizations.ts`.
 * Both carry the real HTTP status, so one predicate covers both.
 *
 * A caller that gets `true` must NOT report a plain failure and must NOT assume
 * success either. Re-read the resource from the server and show what is
 * actually stored.
 */
export function isGatewayTimeoutError(err: unknown): boolean {
  return err instanceof ApiError && GATEWAY_STATUSES.has(err.status);
}

const API_BASE = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");
// -> "http://localhost:8080/api"

export function withBase(url: string) {
  if (/^https?:\/\//i.test(url)) return url; // absolute
  if (!url.startsWith("/")) url = `/${url}`;
  return `${API_BASE}${url}`; // "/agents/.." -> ".../api/agents/.."
}

// ---------------------------------------------------------------------------
// Token refresh with mutex (prevents concurrent refresh calls)
// ---------------------------------------------------------------------------

let refreshPromise: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  try {
    const res = await fetch(withBase("/auth/refresh"), {
      method: "POST",
      credentials: "include",
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Attempt to refresh the access token. Returns true on success.
 * Uses a mutex so concurrent 401s only trigger one refresh call.
 */
async function tryRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = doRefresh().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

// ---------------------------------------------------------------------------
// The tab's account (ADR-0302)
// ---------------------------------------------------------------------------

/**
 * Header naming the account of this tab's URL. The BFF answers 409
 * `account_mismatch` when the session cookie is on another account, so a tab
 * left behind by a switch in another tab is refused instead of being answered
 * (and writing) as the other account.
 */
export const TAB_ACCOUNT_HEADER = "X-Verity-Account";

const TENANT_PATH = /^\/a\/(org_[A-Za-z0-9_-]+)(?=\/|$)/;

/**
 * The account in a `/a/<publicId>/…` path, or null for an unprefixed one.
 *
 * Read from `window.location` at REQUEST time, not from a variable a route
 * component sets: the providers above the route tree (auth, workspace,
 * notifications, watermarks) fetch before any route element mounts.
 */
export function tabAccountFromPath(
  pathname: string = window.location.pathname,
): string | null {
  return TENANT_PATH.exec(pathname)?.[1] ?? null;
}

/**
 * Thrown instead of a response while this tab's account differs from the
 * session's. Deliberately NOT an `ApiError` and without `status`: callers
 * branch on `status === 409` for the `If-Match` precondition ("someone else
 * edited this"), and a mismatch must never read as that. Nothing should show
 * it as an error toast either; the mismatch gate explains the situation.
 */
export class AccountMismatchError extends Error {
  readonly tabAccount: string | null;
  readonly sessionAccount: string | null;

  constructor(conflict: { tabAccount: string | null; sessionAccount: string | null }) {
    super("This tab belongs to a different account than your session.");
    this.name = "AccountMismatchError";
    this.tabAccount = conflict.tabAccount;
    this.sessionAccount = conflict.sessionAccount;
  }
}

export function isAccountMismatchError(err: unknown): err is AccountMismatchError {
  return err instanceof AccountMismatchError;
}

export type SessionConflict = {
  /** The account this tab's URL is in. */
  tabAccount: string | null;
  /** The account the session is known to be on, when known. */
  sessionAccount: string | null;
};

type ConflictListener = (conflict: SessionConflict | null) => void;

let sessionConflict: SessionConflict | null = null;
let conflictController = new AbortController();
const conflictListeners = new Set<ConflictListener>();
const openEventSources = new Set<EventSource>();

/**
 * Stop this tab's transport: while set, no request leaves the tab (pollers
 * above the route tree would otherwise keep earning 409s and eat the shared
 * per-IP rate limit of the user's good tab), every in-flight fetch and
 * fetch-based stream is aborted, and every open `EventSource` is closed.
 */
export function raiseSessionConflict(conflict: SessionConflict): void {
  const first = sessionConflict === null;
  sessionConflict = conflict;
  if (first) {
    conflictController.abort(new AccountMismatchError(conflict));
    for (const source of openEventSources) source.close();
    openEventSources.clear();
  }
  for (const listener of conflictListeners) listener(conflict);
}

/** Resume the transport once the tab and the session agree again. */
export function clearSessionConflict(): void {
  if (sessionConflict === null) return;
  sessionConflict = null;
  conflictController = new AbortController();
  for (const listener of conflictListeners) listener(null);
}

export function getSessionConflict(): SessionConflict | null {
  return sessionConflict;
}

export function subscribeSessionConflict(listener: ConflictListener): () => void {
  conflictListeners.add(listener);
  return () => {
    conflictListeners.delete(listener);
  };
}

/**
 * Session management (`/auth/me`, `/auth/refresh`, `/auth/switch-account`,
 * `/auth/logout`) is how a conflict gets RESOLVED, so it must pass while the
 * transport is stopped. The BFF does not run the tab-account check there
 * either: those routes verify the cookie themselves.
 */
function isSessionManagementUrl(url: string): boolean {
  return url.startsWith(`${API_BASE}/auth/`);
}

/** Only our own API gets the header; a custom header to a third party fails its CORS preflight. */
function isOwnApiUrl(url: string): boolean {
  if (API_BASE && url.startsWith(`${API_BASE}/`)) return true;
  if (url.startsWith("/")) return true;
  try {
    return new URL(url).origin === window.location.origin;
  } catch {
    return false;
  }
}

function withTabAccountHeader(
  headers: HeadersInit | undefined,
  tabAccount: string,
): HeadersInit {
  if (headers instanceof Headers || Array.isArray(headers)) {
    const merged = new Headers(headers);
    merged.set(TAB_ACCOUNT_HEADER, tabAccount);
    return merged;
  }
  // Plain objects stay plain objects: callers and tests read them by key.
  return { ...(headers ?? {}), [TAB_ACCOUNT_HEADER]: tabAccount };
}

function withConflictSignal(signal: AbortSignal | null | undefined): AbortSignal {
  const conflict = conflictController.signal;
  if (!signal) return conflict;
  if (typeof AbortSignal.any === "function") return AbortSignal.any([signal, conflict]);
  // AbortSignal.any is Safari 17.4+; without it every request would throw.
  const combined = new AbortController();
  for (const source of [signal, conflict]) {
    if (source.aborted) {
      combined.abort(source.reason);
      break;
    }
    source.addEventListener("abort", () => combined.abort(source.reason), { once: true });
  }
  return combined.signal;
}

/** An `EventSource` in state CLOSED that never connects and never fires. */
function closedEventSource(url: string, init?: EventSourceInit): EventSource {
  const source = new EventTarget();
  Object.assign(source, {
    url,
    withCredentials: Boolean(init?.withCredentials),
    readyState: 2,
    CONNECTING: 0,
    OPEN: 1,
    CLOSED: 2,
    onopen: null,
    onmessage: null,
    onerror: null,
    close: () => {},
  });
  return source as EventSource;
}

/**
 * Open an `EventSource` through the transport. Native `EventSource` cannot
 * send headers, so these streams are the named header-less path (read-only,
 * keyed by an id of the tab's account). Registering here is what lets the
 * transport close them when the tab's account stops matching the session.
 */
export function openEventSource(url: string, init?: EventSourceInit): EventSource {
  // A stream opened while the transport is stopped is born closed: throwing
  // here would crash the page under the mismatch notice (these are opened in
  // effects), and opening it for real would be a request the stop forbids.
  if (sessionConflict) return closedEventSource(url, init);
  const source = new EventSource(url, init);
  const close = source.close.bind(source);
  source.close = () => {
    openEventSources.delete(source);
    close();
  };
  openEventSources.add(source);
  return source;
}

/**
 * The one place the API is called. Adds the tab-account header, stops while
 * the tab's account conflicts with the session, turns a 409
 * `account_mismatch` into an `AccountMismatchError` before any caller can
 * mistake it for an `If-Match` 409, and handles 401 → refresh → retry (once).
 * Excludes /auth/refresh itself from the refresh loop.
 */
async function fetchWithRefresh(
  url: string,
  init?: RequestInit
): Promise<Response> {
  const sessionUrl = isSessionManagementUrl(url);
  if (sessionConflict && !sessionUrl) throw new AccountMismatchError(sessionConflict);

  const tabAccount = isOwnApiUrl(url) ? tabAccountFromPath() : null;

  const send = async (): Promise<Response> => {
    const res = await fetch(url, {
      credentials: "include",
      ...init,
      headers: tabAccount ? withTabAccountHeader(init?.headers, tabAccount) : init?.headers,
      signal: sessionUrl ? init?.signal : withConflictSignal(init?.signal),
    });
    if (res.status === 409 && tabAccount) {
      const body = (await res.clone().json().catch(() => null)) as {
        error?: { code?: unknown; sessionAccount?: unknown };
      } | null;
      if (body?.error?.code === "account_mismatch") {
        const sessionAccount =
          typeof body.error.sessionAccount === "string" ? body.error.sessionAccount : null;
        const conflict = { tabAccount, sessionAccount };
        if (sessionAccount) reportSessionAccount(sessionAccount);
        raiseSessionConflict(conflict);
        throw new AccountMismatchError(conflict);
      }
    }
    return res;
  };

  let res: Response;
  try {
    res = await send();

    // Don't intercept refresh endpoint itself. A 409 mismatch never gets here:
    // it is a state signal, not an auth failure, and must not reach /login.
    if (res.status === 401 && !url.includes("/auth/refresh")) {
      const refreshed = await tryRefresh();
      if (refreshed) {
        // Retry original request once with new cookies
        return await send();
      }
      // Refresh failed — redirect to login
      window.location.href = "/login";
    }
  } catch (err) {
    // An abort caused by a conflict surfaces as the conflict, not as an
    // AbortError a caller might treat as its own cancellation.
    if (sessionConflict && !sessionUrl && !(err instanceof AccountMismatchError)) {
      throw new AccountMismatchError(sessionConflict);
    }
    throw err;
  }

  return res;
}

/**
 * `fetch` for our API, returning the raw `Response` (streams, non-JSON bodies,
 * callers that inspect the status themselves). Same transport as `apiJson`:
 * tab-account header, conflict stop, 401 refresh. `url` is resolved with
 * `withBase` like every helper here.
 */
export async function apiFetch(url: string, init?: RequestInit): Promise<Response> {
  return fetchWithRefresh(withBase(url), init);
}

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

export type ApiJsonOptions = RequestInit & {
  /** If false, don't auto-unwrap { data: ... } responses. Default: true */
  unwrapData?: boolean;
};

export async function apiJson<T>(url: string, init?: ApiJsonOptions): Promise<T> {
  const fullUrl = withBase(url);
  const { unwrapData = true, ...fetchInit } = init ?? {};

  const res = await fetchWithRefresh(fullUrl, {
    // `...fetchInit` FIRST, then headers: spread after the merged object, a
    // caller's own `headers` (even `{}`) would replace it wholesale and drop
    // the Content-Type, and the BFF then forwards no body at all.
    ...fetchInit,
    headers: {
      ...(fetchInit?.headers || {}),
      ...(fetchInit?.body ? { "Content-Type": "application/json" } : {}),
    },
  });

  // 204 No Content
  if (res.status === 204) {
    return undefined as T;
  }

  const ct = res.headers.get("content-type") || "";

  if (!ct.includes("application/json")) {
    const text = await res.text().catch(() => "");
    // `ApiError`, not a bare `Error`: this branch is where an INFRA error page
    // lands (a 502/504 HTML body from a proxy that never reached the BFF), and
    // the status is the only thing that tells a caller the write's outcome is
    // unknown rather than failed. A bare `Error` buried it in the message
    // string, so `isGatewayTimeoutError` could not see it.
    //
    // The MESSAGE has to be human-readable, and that is not cosmetic. Seven
    // components render `err.message` for an `ApiError` and a clean fallback
    // for anything else (`CampaignsPanel`, `StudioPageCard`, `SurveySetsPanel`,
    // the three activation pages, and `ScheduledFollowupCard`, which returns
    // null for a non-`ApiError`). Before this branch threw an `ApiError` they
    // showed their fallback here; keeping the diagnostic as the message would
    // put `Expected JSON, got text/html (504) from <url>` plus a slice of HTML
    // in front of a customer. The diagnostic is not lost — it moves to `body`,
    // which is where a developer reads it and no UI renders it.
    throw new ApiError(
      GATEWAY_STATUSES.has(res.status)
        ? "Verity could not reach the server. Your last action may not have been saved — reload to see the current state."
        : "Verity got an unexpected response from the server. Please try again.",
      {
        status: res.status,
        body: {
          reason: "non-json-response",
          contentType: ct || "unknown",
          url: fullUrl,
          text: text.slice(0, 200),
        },
      }
    );
  }

  const body = (await res.json().catch(() => ({}))) as ApiEnvelope<T>;

  // ---- Error handling ----
  if (!res.ok) {
    let msg: string;
    const detail = (body as any)?.detail;
    if (Array.isArray(detail) && detail.length > 0) {
      // FastAPI validation errors: extract human-readable msg from first error
      const raw = detail[0]?.msg ?? "Validation error";
      // Strip "Value error, " prefix that Pydantic adds
      msg = raw.replace(/^Value error,\s*/i, "");
    } else if (detail && typeof detail === "object") {
      // FastAPI HTTPException with a dict detail — preserve the object
      // on the thrown ApiError so callers can read structured fields
      // (e.g. missing_scopes on the reauth endpoint).
      msg = String(
        (detail as any).message ?? (body as any)?.error?.message ?? "Request failed",
      );
    } else {
      msg = String(body?.error?.message ?? detail ?? body?.message ?? "Request failed");
    }

    throw new ApiError(msg, { status: res.status, detail, body });
  }

  // ---- unwrap { data: ... } if present (unless disabled) ----
  // Consequence for backend DTOs: a response body with a top-level `data`
  // key IS an envelope here, whatever the backend meant. A DTO that names one
  // of its own fields `data` (a hook payload, a rows array) reaches the page
  // as that field alone, with the sibling fields silently gone — no error,
  // only a wrong shape. Found in the browser on 2026-09-03: the report block
  // preview lost its `parameters`. Name such fields `payload`/`rows`/…, or
  // call with `unwrapData: false` and say why. Six other backend DTOs carry a
  // top-level `data` today (orchestration board, 2026-09-03).
  if (unwrapData && body && typeof body === "object" && "data" in body) {
    return body.data as T;
  }

  // ---- fallback: raw body ----
  return body as unknown as T;
}

/**
 * POST/GET an endpoint that answers with BINARY and return the Blob.
 *
 * `apiJson` cannot be used for these: it throws "Expected JSON" the moment the
 * content type is anything else, which is exactly what a working .xlsx
 * response looks like.
 *
 * Goes through `fetchWithRefresh` like every other helper here. That is
 * load-bearing rather than tidiness: a hand-rolled `fetch` would skip the
 * 401 → refresh → retry mutex, so an export started just after the access
 * token expired would bounce the user to /login instead of refreshing.
 *
 * On a non-2xx the JSON error body is parsed and thrown as `ApiError` WITH
 * `status` set, because callers branch on it (413 → "too much data for
 * Excel"). Errors come back as JSON from the BFF even when the success path
 * is binary.
 */
export async function apiBlob(url: string, init?: RequestInit): Promise<Blob> {
  const fullUrl = withBase(url);

  const res = await fetchWithRefresh(fullUrl, {
    // `...init` FIRST, then headers, like every helper in this file: spread
    // the other way around, a caller's own `headers` replaces the merged
    // object wholesale and the Content-Type is gone (client.headers.test.ts).
    ...init,
    headers: {
      ...(init?.headers || {}),
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
    },
  });

  if (!res.ok) {
    let detail: unknown;
    let body: unknown;
    let msg = "Request failed";
    try {
      body = await res.json();
      const b = body as ApiEnvelope & { error?: unknown };
      detail = (b as { detail?: unknown })?.detail;
      const errorField = (b as { error?: unknown })?.error;
      msg = String(
        (typeof errorField === "string" ? errorField : undefined) ??
          (errorField as { message?: string } | undefined)?.message ??
          (typeof detail === "string" ? detail : undefined) ??
          (b as { message?: string })?.message ??
          "Request failed",
      );
    } catch {
      // Body was not JSON (a proxy error page, a truncated response). The
      // status still carries the branch the caller needs, so throw with it.
    }
    throw new ApiError(msg, { status: res.status, detail, body });
  }

  return res.blob();
}

/**
 * Wrapper helper: verwacht { data: ... } en returnt direct data.
 */
export async function apiData<T = unknown>(
  url: string,
  init?: RequestInit
): Promise<T> {
  const fullUrl = withBase(url);

  const res = await fetchWithRefresh(fullUrl, {
    cache: "no-store",
    // `...init` after the defaults it may override (`cache`), before the
    // headers it must not replace (see `apiJson`).
    ...init,
    headers: {
      Accept: "application/json",
      ...(init?.headers || {}),
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
    },
  });

  if (res.status === 204 || res.status === 304) return undefined as T;

  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Expected JSON, got ${ct} (${res.status}) from ${fullUrl}\n${text.slice(0, 200)}`
    );
  }

  const body = (await res.json().catch(() => ({}))) as any;

  if (!res.ok) {
    throw new Error(
      body?.error?.message ?? body?.detail ?? body?.message ?? "Request failed"
    );
  }

  return body as T;
}

/**
 * Voor endpoints die 204 teruggeven (DELETE etc).
 */
export async function apiNoContent(
  url: string,
  init?: RequestInit
): Promise<void> {
  const fullUrl = withBase(url);

  const res = await fetchWithRefresh(fullUrl, {
    // `...init` FIRST, then headers (see `apiJson`).
    ...init,
    headers: {
      ...(init?.headers || {}),
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
    },
  });

  if (res.ok) return;

  // als er JSON error is, probeer die te lezen
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    const body = (await res.json().catch(() => ({}))) as ApiErrorShape;
    // BFF admin routes return `{ error: "<message>" }` (a plain string), while
    // others return `{ error: { message } }`. Handle both so the server's
    // user-facing safeguard messages aren't collapsed to "Request failed".
    const msg =
      (typeof body?.error === "string" ? body.error : body?.error?.message) ??
      body?.detail ??
      body?.message ??
      "Request failed";
    // `ApiError`, not a plain `Error`: a caller that deletes several things
    // at once has to tell "already gone" (404) and "slow down" (429) from a
    // real refusal, and the status is the only thing that says which.
    throw new ApiError(String(msg), {
      status: res.status,
      detail: body?.detail,
      body,
    });
  }

  const text = await res.text().catch(() => "");
  throw new ApiError(
    `Request failed (${res.status}) from ${fullUrl}\n${text.slice(0, 200)}`,
    { status: res.status },
  );
}
